// eslint-disable-next-line import/no-named-as-default
import convert, { MeasureKind, UnitsByMeasure } from 'convert';
import { IntlShape } from 'react-intl';
import {
  CM_TO_INTERNAL_FACTOR,
  INTERNAL_TO_CM_FACTOR,
  INTERNAL_TO_IN_FACTOR,
  IN_TO_INTERNAL_FACTOR,
  UNIT_TO_METER_DIVISOR
} from './constants';
 
/**
 * Converts a number value in cm to a representation in our internal unit system.
 *
 * @param {number} value value in cm that needs to get converted to internal units.
 * @returns {number} the value represented in our internal units, rounded to the nearest integer.
 */
export const cmToInternalRounded = (value: number): number =>
  Math.round(value * CM_TO_INTERNAL_FACTOR * UNIT_TO_METER_DIVISOR) / UNIT_TO_METER_DIVISOR;

/**
 * Converts a value represented in our internal units to a representation of that value in cm.
 *
 * @param {number} value value in internal units that needs to get converted to cm.
 * @returns {number} the value represented in cm, rounded to the nearest integer.
 */
export const internalToCentimetersRounded = (value: number): number => Math.round(value * INTERNAL_TO_CM_FACTOR);

/**
 * Converts a number value in inches to a representation in our internal unit system.
 *
 * @param {number} value value in inch that needs to get converted to internal units.
 * @returns {number} the value represented in our internal units, rounded to the nearest integer.
 */
export const inchToInternalRounded = (value: number): number =>
  Math.round(value * IN_TO_INTERNAL_FACTOR * UNIT_TO_METER_DIVISOR) / UNIT_TO_METER_DIVISOR;

/**
 * Converts a value represented in our internal units to a representation of that value in inches.
 *
 * @param {number} value value in internal units that needs to get converted to inches.
 * @returns {number} the value represented in inches, rounded to the nearest integer.
 */
export const internalToInchesRounded = (value: number): number => Math.round(value * INTERNAL_TO_IN_FACTOR);

/**
 * Converts a number value in centimeters (cm) to inches, rounding to the nearest standard inch fraction.
 *
 * @param {number} value - The value in cm that needs to be converted to inches.
 * @returns {number} - The value represented in inches, rounded to the nearest inch fraction.
 */
export const cmToInchesRounded = (value: number): number => Math.round(value / 2.54);

/**
 * Method to convert any given numerical value from one unit type to another quickly.
 * Works between metric and imperial as well.
 *
 * @param {number} value amount that needs to get converted from one unit to another.
 * @param {UnitsByMeasure<MeasureKind>} originUnit unit type of the original value.
 * @param {UnitsByMeasure<MeasureKind>} targetUnit unit type that is needed.
 * @returns {number} the original value, converted into the target unit.
 */
export const convertTo = (value: number, originUnit: UnitsByMeasure<MeasureKind>, targetUnit: UnitsByMeasure<MeasureKind>): number =>
  convert(value, originUnit).to(targetUnit) as unknown as number;

/**
 * Function to get the localized unit type as a string to use in labels when displaying numbers.
 *
 * @param {IntlShape} intl the intl object to get access to the localisation properties.
 * @param {string} unit string representation of the unit that needs to have a localized label. Note that this needs to get written out as an individual unit. (e.g. "meter", "centimeter", "foot", etc.)
 * @returns {string} localized label of that unit type.
 */
export const getLocalizedUnitLabel = (intl: IntlShape, unit: string): string =>
  intl.formatNumber(0, { style: 'unit', unit: unit, unitDisplay: 'short' }).substring(2);

/**
 * Function to convert a given number from an origin unit into another unit.
 * Creates a localized label with the converted value and the unit type attached to the label.
 *
 * @param {IntlShape} intl the translation handling object.
 * @param {number} value the number that needs to get converted and have its label localized.
 * @param {UnitsByMeasure<MeasureKind>} originUnit the origin unit from which the value needs to get converted.
 * @param {UnitsByMeasure<MeasureKind>} targetUnit the target unit into which the vale needs to get converted.
 * @returns {string} containing the converted number with the localized label of the target unit attached to it.
 */
export const convertNumberToLocalizedUnit = (
  intl: IntlShape,
  value: number,
  originUnit: UnitsByMeasure<MeasureKind>,
  targetUnit: UnitsByMeasure<MeasureKind>,
): string => {
  const convertedValue = convertTo(value, originUnit, targetUnit);
  return intl.formatNumber(convertedValue, {
    style: 'unit',
    unit: targetUnit,
    unitDisplay: 'short',
    useGrouping: 'auto',
  });
};
